import { useCallback, useState } from 'react';
import { API_BASE } from '../../../config/constants';

export interface DocModel {
    id?: string | number;
    url: string;
    name: string;
    contentType?: string;
    size?: number;
    lastModified?: string;
    etag?: string;
    fileName?: string;
    [key: string]: any;
}

export interface PickedFileDescriptor {
    file: File;
    name: string;
    path: string;
    bytes?: ArrayBuffer;
}

interface UseDocsParams {
    userId?: string | number | null;
    objectId?: string | number | null;
}

export function useDocs({ userId = null, objectId = null }: UseDocsParams = {}) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [docs, setDocs] = useState<DocModel[]>([]);
    const [allDocs, setAllDocs] = useState<DocModel[]>([]);
    const [filteredDocs, setFilteredDocs] = useState<DocModel[]>([]);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            let endpoint = '';
            let body: Record<string, string | number> = {};

            if (userId !== null && userId !== undefined) {
                endpoint = `${API_BASE}/api/objects/get-user-docs`;
                body = { user_id: userId };
            } else if (objectId !== null && objectId !== undefined) {
                endpoint = `${API_BASE}/api/objects/get-object-docs`;
                body = { object_id: objectId };
            } else {
                setDocs([]);
                setAllDocs([]);
                setFilteredDocs([]);
                return;
            }

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });

            if (!response.ok) {
                throw new Error('Failed to load documents');
            }

            const result = await response.json();
            const loadedDocs: DocModel[] = result?.success && Array.isArray(result?.data)
                ? result.data
                : [];

            setDocs(loadedDocs);
            setAllDocs(loadedDocs);
            setFilteredDocs(loadedDocs);
        } catch (err: any) {
            setError(err.message || 'Failed to load documents');
            setDocs([]);
            setAllDocs([]);
            setFilteredDocs([]);
        } finally {
            setLoading(false);
        }
    }, [userId, objectId]);

    const openDocument = useCallback(
        async (fileUrl: string, options?: { fileName?: string; contentType?: string; objectName?: string }) => {
            const fileName = String(options?.fileName ?? '').toLowerCase();
            const contentType = String(options?.contentType ?? '').toLowerCase();
            const isPdf = fileName.endsWith('.pdf') || contentType.includes('pdf');

            if (!isPdf) {
                window.open(fileUrl, '_blank', 'noopener,noreferrer');
                return;
            }

            try {
                const response = await fetch(fileUrl);
                if (!response.ok) {
                    throw new Error('Failed to load PDF file');
                }

                const pdfBytes = await response.arrayBuffer();
                const { PDFDocument, rgb, degrees } = await import('pdf-lib');
                const pdfDoc = await PDFDocument.load(pdfBytes);
                const pages = pdfDoc.getPages();

                const authTokenStr = localStorage.getItem('AUTH_TOKEN');
                const authUser = authTokenStr ? JSON.parse(authTokenStr) : {};
                const userEmail = String(authUser?.email ?? authUser?.user_email ?? 'User');
                const objectName = String(options?.objectName ?? '').trim().slice(0, 50);
                const watermarkText = objectName ? `${userEmail} - ${objectName}` : userEmail;

                pages.forEach((page) => {
                    const { width, height } = page.getSize();
                    page.drawText(watermarkText, {
                        x: width * 0.2,
                        y: height * 0.45,
                        size: Math.max(14, Math.min(width, height) * 0.03),
                        color: rgb(0.55, 0.55, 0.55),
                        opacity: 0.3,
                        rotate: degrees(45),
                    });
                });

                const watermarkedPdf = await pdfDoc.save();
                const blob = new Blob([new Uint8Array(watermarkedPdf)], { type: 'application/pdf' });
                const objectUrl = URL.createObjectURL(blob);
                window.open(objectUrl, '_blank', 'noopener,noreferrer');
                setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
            } catch (err) {
                console.error('Failed to watermark PDF:', err);
                window.open(fileUrl, '_blank', 'noopener,noreferrer');
            }
        },
        []
    );

    const pickFile = useCallback(async (files: FileList | null): Promise<PickedFileDescriptor | null> => {
        if (!files || files.length === 0) return null;

        const file = files[0];
        const bytes = await file.arrayBuffer();

        return {
            file,
            name: file.name,
            path: file.name,
            bytes,
        };
    }, []);

    const createObjectMedia = useCallback(
        async (targetObjectId: string | number, file: File, baseDirectory = 'objects'): Promise<boolean> => {
            const traceId = `objmedia-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            try {
                const authTokenStr = localStorage.getItem('AUTH_TOKEN');
                const authUser = authTokenStr ? JSON.parse(authTokenStr) : {};
                const creatorId = authUser?.id ?? authUser?.user_id ?? authUser?.userId ?? 0;
                const creatorType = authUser?.role || 'User';

                const docUrl = `${baseDirectory}/${targetObjectId}/${file.name}`;

                const query = new URLSearchParams({
                    object_id: String(targetObjectId),
                    doc_url: docUrl,
                    file_name: file.name,
                    creator_id: String(creatorId),
                    creator_type: String(creatorType),
                    container_name: 'docs',
                    directory_name: `${baseDirectory}/${targetObjectId}`,
                    trace_id: traceId,
                }).toString();

                const endpoint = `${API_BASE}/api/objects/create-object-media?${query}`;
                console.log('[HDocs] createObjectMedia request', {
                    traceId,
                    targetObjectId,
                    fileName: file.name,
                    docUrl,
                    endpoint,
                });

                const formData = new FormData();
                formData.append('file', file);

                const response = await fetch(endpoint, {
                    method: 'POST',
                    body: formData,
                });

                console.log('[HDocs] createObjectMedia response status', {
                    traceId,
                    status: response.status,
                    ok: response.ok,
                    targetObjectId,
                    docUrl,
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    console.error('[HDocs] createObjectMedia failed', {
                        traceId,
                        targetObjectId,
                        docUrl,
                        status: response.status,
                        errorText,
                    });
                    throw new Error(errorText || 'Failed to create object media');
                }

                const responseText = await response.text();
                console.log('[HDocs] createObjectMedia success', {
                    traceId,
                    targetObjectId,
                    docUrl,
                    responseText,
                });

                return true;
            } catch (err: any) {
                console.error('[HDocs] createObjectMedia exception', {
                    traceId,
                    targetObjectId,
                    fileName: file?.name,
                    message: err?.message,
                });
                setError(err.message || 'Failed to create object media');
                return false;
            }
        },
        []
    );

    const uploadDocumentToAzure = useCallback(
        async (pickedFile: PickedFileDescriptor | null, targetObjectId: string | number): Promise<boolean> => {
            if (!pickedFile) {
                console.warn('[HDocs] uploadDocumentToAzure called without pickedFile', { targetObjectId });
                return false;
            }

            console.log('[HDocs] uploadDocumentToAzure start', {
                targetObjectId,
                fileName: pickedFile.name,
            });

            const success = await createObjectMedia(targetObjectId, pickedFile.file, 'objects');
            if (success) {
                await loadData();
            }

            console.log('[HDocs] uploadDocumentToAzure done', {
                targetObjectId,
                fileName: pickedFile.name,
                success,
            });
            return success;
        },
        [createObjectMedia, loadData]
    );

    const uploadUserDocumentToAzure = useCallback(
        async (pickedFile: PickedFileDescriptor | null, targetUserId: string | number): Promise<boolean> => {
            if (!pickedFile) return false;
            const success = await createObjectMedia(targetUserId, pickedFile.file, 'users');
            if (success) {
                await loadData();
            }
            return success;
        },
        [createObjectMedia, loadData]
    );

    const deleteDocumentFromAzure = useCallback(async (documentId: string | number): Promise<boolean> => {
        try {
            const response = await fetch(
                `${API_BASE}/api/objects/delete-document-by-id?document_id=${encodeURIComponent(String(documentId))}`,
                { method: 'POST' }
            );

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || 'Failed to delete document');
            }

            await loadData();
            return true;
        } catch (err: any) {
            setError(err.message || 'Failed to delete document');
            return false;
        }
    }, [loadData]);

    const renameFile = useCallback(async (documentId: string | number, newFileName: string): Promise<boolean> => {
        try {
            const query = new URLSearchParams({
                document_id: String(documentId),
                file_name: newFileName,
            }).toString();

            const response = await fetch(`${API_BASE}/api/objects/update-file-name?${query}`, {
                method: 'POST',
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || 'Failed to rename file');
            }

            await loadData();
            return true;
        } catch (err: any) {
            setError(err.message || 'Failed to rename file');
            return false;
        }
    }, [loadData]);

    const filterDocs = useCallback(
        (query: string) => {
            if (!query.trim()) {
                setFilteredDocs(docs);
                return;
            }

            const q = query.toLowerCase();
            setFilteredDocs(
                docs.filter((doc) => {
                    const name = String(doc.fileName ?? doc.name ?? '').toLowerCase();
                    return name.includes(q);
                })
            );
        },
        [docs]
    );

    const fetchObjectDataroom = useCallback(async (targetObjectId: string | number) => {
        try {
            const response = await fetch(`${API_BASE}/api/objects/get-object-dataroom`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ object_id: targetObjectId }),
            });

            if (!response.ok) throw new Error('Failed to fetch object dataroom');
            const result = await response.json();
            return result;
        } catch (err: any) {
            setError(err.message || 'Failed to fetch object dataroom');
            return null;
        }
    }, []);

    const fetchObjectDocs = useCallback(async (targetObjectId: string | number) => {
        try {
            const response = await fetch(`${API_BASE}/api/objects/get-object-docs`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ object_id: targetObjectId }),
            });

            if (!response.ok) throw new Error('Failed to fetch object docs');
            const result = await response.json();
            return result;
        } catch (err: any) {
            setError(err.message || 'Failed to fetch object docs');
            return null;
        }
    }, []);

    return {
        loading,
        error,
        docs,
        allDocs,
        filteredDocs,
        loadData,
        openDocument,
        pickFile,
        uploadDocumentToAzure,
        uploadUserDocumentToAzure,
        deleteDocumentFromAzure,
        renameFile,
        filterDocs,
        fetchObjectDataroom,
        fetchObjectDocs,
        createObjectMedia,
    };
}
